/**
 * Modul-Register — zentrale Quelle aller Kachel-Module.
 *
 * Eintraege werden in zwei Stellen konsumiert:
 *   1. HomePage.js  — rendert Kacheln (nur tile_in_homepage === true)
 *   2. App.js       — renderCurrentView prueft getModule(currentView) als
 *                     ersten Schritt vor dem bestehenden Switch-Fallback.
 *
 * licenseFlag:      null   => immer aktiv (kein RBAC-Check)
 *                   string => user.role==='admin' ODER user.permissions?.[flag]
 * licenseModuleKey: null   => immer lizenziert
 *                   string => Backend-Modul-Key aus /api/license.licensedModules;
 *                             mode='grace' => alle als lizenziert gewertet.
 *                             Lizenz ist AEUSSERE Schranke — gilt auch fuer Admin.
 *
 * KANONISCHE MAP (muss mit backend/core/config/modules.js uebereinstimmen):
 *   procurement  -> frontendId='catalogs',        licenseModuleKey='procurement'
 *   warehouse    -> frontendId='lager',            licenseModuleKey='warehouse'
 *   einvoicing   -> frontendId='einvoicing',       licenseModuleKey='einvoicing'
 *   pm           -> frontendId='plant-maintenance',licenseModuleKey='pm'
 *
 * KONVENTIONEN
 *   - Kein react-router, kein direkter Import von View-Komponenten hier.
 *     Die Views werden lazy per getModule(id).View referenziert.
 *   - Neue Module additiv anhaengen — bestehende Eintraege nicht umordnen.
 *   - Jeder neue Eintrag braucht i18n-Keys in de.js UND en.js.
 */

import { ShoppingCart, Warehouse, Receipt, Wrench, PackagePlus, ClipboardCheck } from 'lucide-react';

import CatalogView             from '../components/CatalogView';
import LagerShell              from '../components/LagerShell';
// E-Invoicing: das echte Modul (modules/einvoicing/) — ersetzt den frueheren
// "in Entwicklung"-Platzhalter aus components/einvoicing/.
import EInvoicingView          from './einvoicing/EInvoicingView';
// PM: das echte Modul (modules/plant-maintenance/) — ersetzt den frueheren
// "in Entwicklung"-Platzhalter aus components/plantMaintenance/.
import PlantMaintenanceView    from './plant-maintenance/PlantMaintenanceView';
import MaterialErfassungView   from '../components/materialErfassung/MaterialErfassungView';
// Auftragsbestaetigung: das echte Modul (modules/auftragsbestaetigung/) — ersetzt den
// frueheren "in Entwicklung"-Platzhalter aus components/auftragsbestaetigung/.
import OrderConfirmationView   from './auftragsbestaetigung/AuftragsbestaetigungView';

/**
 * MODULES — alle Kachel-Module die sowohl in der HomePage erscheinen als auch
 * ueber das Register in App.js geroutet werden koennen.
 *
 * Felder:
 *   id               {string}      — entspricht dem currentView_id in App.js
 *   licenseModuleKey {string|null} — Backend-Modul-Key aus /api/license.licensedModules;
 *                                    null = immer lizenziert (keine Lizenzpruefung)
 *   licenseFlag      {string|null} — Schluessel in user.permissions; null = immer aktiv
 *   tile             {object}      — Kachel-Konfiguration fuer HomePage
 *     titleKey       {string}      — i18n-Key fuer den Kacheltitel
 *     descKey        {string}      — i18n-Key fuer die Kachelbeschreibung
 *     Icon           {React.FC}    — Lucide-Icon-Komponente
 *     color          {string}      — Farb-Token fuer getColorClasses()
 *     badge          {object?}     — optionale Kachel-Plakette { labelKey, tone };
 *                                    tone: 'new' | 'compliance' (HomePage erzeugt
 *                                    intern zusaetzlich 'preview' fuer Preview-Kacheln)
 *   View             {React.FC}    — die View-Komponente
 *   tile_in_homepage {boolean}     — ob eine Kachel auf der HomePage erscheint
 */
export const MODULES = [
  {
    id: 'catalogs',
    licenseModuleKey: 'procurement',
    licenseFlag: null,
    tile: {
      titleKey: 'home.procurement',
      descKey:  'home.procurementDesc',
      Icon:     ShoppingCart,
      color:    'blue',
    },
    View: CatalogView,
    tile_in_homepage: true,
  },
  {
    id: 'lager',
    licenseModuleKey: 'warehouse',
    licenseFlag: 'can_access_warehouse',
    tile: {
      titleKey: 'home.warehouse',
      descKey:  'home.warehouseDesc',
      Icon:     Warehouse,
      color:    'blue',
    },
    View: LagerShell,
    tile_in_homepage: true,
  },
  {
    id: 'einvoicing',
    licenseModuleKey: 'einvoicing',
    licenseFlag: 'can_access_einvoicing',
    tile: {
      titleKey: 'home.einvoicing',
      descKey:  'home.einvoicingDesc',
      Icon:     Receipt,
      color:    'violet',
      badge:    { labelKey: 'home.einvoicingBadge', tone: 'compliance' },
    },
    View: EInvoicingView,
    tile_in_homepage: true,
  },
  {
    id: 'plant-maintenance',
    licenseModuleKey: 'pm',
    licenseFlag: 'can_access_pm',
    tile: {
      titleKey: 'home.plantMaintenance',
      descKey:  'home.plantMaintenanceDesc',
      Icon:     Wrench,
      color:    'amber',
    },
    View: PlantMaintenanceView,
    tile_in_homepage: true,
  },
  {
    // Material-Schnellerfassung (Owner: Taras) — reduzierte Felder fuer schnelle
    // Materialanlage. Gegated: licenseModuleKey 'material' (LicenseGate-Scope in
    // backend/core/config/modules.js) + RBAC-Flag can_access_material (Admin-Bypass).
    id: 'material-schnellerfassung',
    licenseModuleKey: 'material',
    licenseFlag: 'can_access_material',
    tile: {
      titleKey: 'home.materialEntry',
      descKey:  'home.materialEntryDesc',
      Icon:     PackagePlus,
      color:    'emerald',
    },
    View: MaterialErfassungView,
    tile_in_homepage: true,
  },
  {
    // Auftragsbestaetigung erfassen (Owner: Taras) — Lieferanten-AB per OCR-Sidecar
    // erfassen und feldweise gegen den MM-Bestell-Snapshot abgleichen (Phase 1:
    // Upload -> OCR -> Pruef-UI -> Freigabe/Ablehnung; kein SAP-Writeback).
    id: 'auftragsbestaetigung',
    licenseModuleKey: 'order-confirmation',
    licenseFlag: 'can_access_order_confirmation',
    tile: {
      titleKey: 'home.orderConfirmation',
      descKey:  'home.orderConfirmationDesc',
      Icon:     ClipboardCheck,
      color:    'indigo',
    },
    View: OrderConfirmationView,
    tile_in_homepage: true,
  },
];

/**
 * Gibt das Modul-Objekt fuer eine gegebene View-ID zurueck oder undefined.
 *
 * @param {string} id — currentView_id (z. B. 'lager', 'einvoicing')
 * @returns {object|undefined}
 */
export const getModule = (id) => MODULES.find((m) => m.id === id);

/**
 * Prueft ob ein Nutzer Zugriff auf ein Modul hat.
 *
 * Pruef-Reihenfolge (spiegelt Backend licenseGate.js wider):
 *   1. Lizenz-Check (AEUSSERE Schranke, gilt auch fuer Admin):
 *      - licenseModuleKey === null => immer lizenziert
 *      - licenseState.mode === 'grace' => alle Module als lizenziert gewertet
 *      - licenseState.licensedModules.has(licenseModuleKey) === false => kein Zugriff
 *      - licenseState fehlt/nicht geladen => optimistisch durchlassen (grace analog)
 *   2. RBAC-Check:
 *      - licenseFlag === null => immer aktiv (kein RBAC-Check)
 *      - user.role === 'admin' => Admin-Bypass fuer RBAC (nicht fuer Lizenz!)
 *      - user.permissions?.[licenseFlag] === true => Zugriff
 *
 * @param {object}      module       — Eintrag aus MODULES
 * @param {object}      user         — user-Objekt aus useAuth / App-State
 * @param {object|null} licenseState — { licensedModules: Set<string>, mode: 'ok'|'grace' }
 *                                     oder null wenn noch nicht geladen (=> grace-Verhalten)
 * @returns {boolean}
 */
export const hasModuleAccess = (module, user, licenseState = null) => {
  if (!module) return false;

  // --- 1. Lizenz-Check (aeussere Schranke) ---
  if (module.licenseModuleKey !== null && module.licenseModuleKey !== undefined) {
    // Noch nicht geladen oder grace-Mode => optimistisch durchlassen
    const isGrace = !licenseState || licenseState.mode === 'grace';
    if (!isGrace) {
      const licensed = licenseState.licensedModules instanceof Set
        ? licenseState.licensedModules.has(module.licenseModuleKey)
        : Array.isArray(licenseState.licensedModules)
          ? licenseState.licensedModules.includes(module.licenseModuleKey)
          : false;
      if (!licensed) return false;
    }
  }

  // --- 2. RBAC-Check ---
  if (module.licenseFlag === null) return true;
  if (!user) return false;
  if (user.role === 'admin') return true;
  return !!user.permissions?.[module.licenseFlag];
};
